<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import { messageStamp } from 'shared/helpers/timeHelper';
import CreateEventDefinitionDialog from './create/CreateEventDefinitionDialog.vue';
import EditEventDefinitionDialog from './edit/EditEventDefinitionDialog.vue';
import DeleteEventDefinitionDialog from './delete/DeleteEventDefinitionDialog.vue';

const { t } = useI18n();
const store = useStore();

const createDialogRef = ref(null);
const editDialogRef = ref(null);
const deleteDialogRef = ref(null);

const definitions = useMapGetter('wgptEventDefinitions/getEventDefinitions');
const uiFlags = useMapGetter('wgptEventDefinitions/getUIFlags');

const tableHeaders = computed(() => [
  t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.LIST.NAME'),
  t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.LIST.PROPERTIES'),
  t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.LIST.UPDATED_ON'),
]);

onMounted(() => {
  store.dispatch('wgptEventDefinitions/get');
});

function startCreate() {
  createDialogRef.value?.open();
}

function propertyCount(definition) {
  return Object.keys(definition.schema?.properties || {}).length;
}

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');

defineExpose({ startCreate });
</script>

<template>
  <div>
    <woot-loading-state v-if="uiFlags.isFetching" />

    <p
      v-else-if="!definitions.length"
      class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
    >
      {{ t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.EMPTY_STATE.TITLE') }}
    </p>

    <BaseTable v-else :headers="tableHeaders" :items="definitions">
      <template #row="{ items }">
        <BaseTableRow v-for="def in items" :key="def.id" :item="def">
          <template #default>
            <BaseTableCell class="max-w-0 w-full">
              <div class="flex flex-col items-start gap-1">
                <span class="block text-heading-3 text-n-slate-12">
                  {{ def.name }}
                </span>
                <p
                  v-if="def.description"
                  class="mb-0 text-n-slate-11 text-body-main truncate max-w-xs"
                >
                  {{ def.description }}
                </p>
              </div>
            </BaseTableCell>

            <BaseTableCell>
              <span class="text-body-main text-n-slate-11 whitespace-nowrap">
                {{ propertyCount(def) }}
                {{
                  t(
                    'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.LIST.PROPERTIES_COUNT'
                  )
                }}
              </span>
            </BaseTableCell>

            <BaseTableCell :title="readableDateWithTime(def.updated_at)">
              <span class="text-body-main text-n-slate-12 whitespace-nowrap">
                {{ readableDate(def.updated_at) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="
                    $t(
                      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.LIST.TOOLTIP.EDIT'
                    )
                  "
                  icon="i-woot-edit-pen"
                  sm
                  slate
                  @click.stop="editDialogRef.open(def)"
                />
                <Button
                  v-tooltip.top="
                    $t(
                      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.LIST.TOOLTIP.DELETE'
                    )
                  "
                  icon="i-woot-bin"
                  sm
                  slate
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click.stop="deleteDialogRef.open(def)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <CreateEventDefinitionDialog ref="createDialogRef" />
    <EditEventDefinitionDialog ref="editDialogRef" />
    <DeleteEventDefinitionDialog ref="deleteDialogRef" />
  </div>
</template>
